import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button, OutboundLink } from '@higgins/ui';
import { NearmapFramer } from '../components/nearmap-framer';
import { SitePlanView } from '../components/site-plan-view';
import { AccessPlanView } from '../components/access-plan-view';
import { MarkedUpStatusLine } from '../components/marked-up-status';
import { ACCESS_WORDING } from '../lib/plan-copy-wording';
import { ErrorMessage, Notice } from '../components/message';
import { addEarlierPhotos, evidenceOf, placePin, visitOf } from '../lib/condition-records';
import { pinsFor } from '../lib/site-plan-pins';
import { getJob, listRecords } from '../lib/device-db';
import { useDeviceQuery, useJobRefresh, useOnline } from '../lib/hooks';
import { useMarkedUpPlan } from '../lib/marked-up-plan';
import { accessLinesOf } from '../lib/access-lines';
import { addAccessLine, removeAccessLine, useAccessPlan } from '../lib/access-plan';
import { armLocation, positionNow } from '../lib/location';
import { parseSitePlan, PlanFileMissing, placeGpsPins, planImage, renderNearmap, savePickedPlan, savePlan, setPinBearing } from '../lib/site-plan';
import type { PlanFrame } from '../lib/site-plan-math';

/** The visit's plan file was deleted or recycled on SharePoint: say so, and offer the way back (a new plan). */
function PlanFileMissingNotice({ online, onMakeNew }: { online: boolean; onMakeNew: () => void }) {
  return (
    <Notice>
      <p>The saved site plan is no longer on the job&rsquo;s SharePoint site: it may have been deleted or moved there. Make a new plan; photos with a location are pinned again on a Nearmap plan, the rest by hand.</p>
      <Button className="mt-2 h-11" disabled={!online} onClick={onMakeNew}>
        Make a new plan
      </Button>
      {!online && <p className="mt-1 text-xs">Making a plan needs signal.</p>}
    </Notice>
  );
}

/** The plan's two modes: photo pins, or access lines along the External areas. */
type PlanMode = 'pins' | 'access';

/** A job's site plan (once per job), with a pin for each external photo and the access lines along its External areas. */
export default function SitePlanPage() {
  const { jobId = '' } = useParams();
  const online = useOnline();
  const data = useDeviceQuery(async () => ({ job: await getJob(jobId), records: await listRecords(jobId) }), [jobId]);
  useJobRefresh(data?.job?.number);
  const visit = data ? visitOf(data.records) : undefined;
  const plan = parseSitePlan(visit?.fields.hig_siteplan);
  const [image, setImage] = useState<string | null>(null);
  /** Making a new plan: choosing its source, or framing Nearmap. */
  const [making, setMaking] = useState<'choose' | 'nearmap' | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The visit's plan file is gone from SharePoint. */
  const [fileMissing, setFileMissing] = useState(false);
  const [placing, setPlacing] = useState<string | null>(null);
  const [mode, setMode] = useState<PlanMode>('pins');
  const [, setTick] = useState(0);

  useEffect(armLocation, []);

  // Photos taken before the report started get their evidence (and so their pins).
  useEffect(() => {
    if (visit) void addEarlierPhotos(jobId).catch(console.error);
  }, [jobId, visit?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- once per visit

  // The plan's image: this device's copy, else from SharePoint.
  useEffect(() => {
    if (!visit || !plan) return;
    setFileMissing(false);
    // Only the answer for the plan on screen counts: a late one for an earlier plan is dropped (PR #505).
    let current = true;
    planImage(visit.id, plan).then((loaded) => current && setImage(loaded), (err: unknown) => {
      if (!current) return;
      if (err instanceof PlanFileMissing) {
        // The previous plan's image may still be here: it isn't this plan's (PR #505).
        setImage(null);
        setFileMissing(true);
      } else setProblem(err instanceof Error ? err.message : String(err));
    });
    return () => {
      current = false;
    };
  }, [visit?.id, plan?.driveItemId, plan?.savedOn]); // eslint-disable-line react-hooks/exhaustive-deps -- a new plan, not every re-read

  // On a Nearmap plan, photos with GPS get their pins placed for the measurer.
  const evidence = data ? evidenceOf(data.records) : [];
  const unplaced = evidence.filter((e) => typeof e.fields.hig_pinx !== 'number' && typeof e.fields.hig_latitude === 'number').length;
  useEffect(() => {
    if (plan && unplaced) void placeGpsPins(plan, evidence);
  }, [plan?.driveItemId, plan?.savedOn, unplaced]); // eslint-disable-line react-hooks/exhaustive-deps -- a new plan, or new photos with a position

  const pins = data ? pinsFor(data.records) : [];
  const markedUp = useMarkedUpPlan(visit, plan, image, pins.map((p) => p.pin));
  const accessLines = data ? accessLinesOf(data.records) : [];
  const access = useAccessPlan(visit, plan, image, data?.records ?? [], accessLines.map((l) => l.line));
  const externalAreas = (data?.records ?? []).filter((r) => r.table === 'area' && r.fields.hig_side === 'External').sort((a, b) => Number(a.fields.hig_sortorder ?? 0) - Number(b.fields.hig_sortorder ?? 0));

  if (!data) return null;
  if (!data.job || !visit) {
    return (
      <p className="text-sm text-slate-600">
        Start the condition report on the job&rsquo;s Condition tab first. <Link to={`/job/${jobId}`} className="text-primary underline">Back to the job</Link>
      </p>
    );
  }

  async function run(work: () => Promise<void>) {
    setSaving(true);
    setProblem(null);
    try {
      await work();
      setMaking(null);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const saveNearmap = (frame: PlanFrame, onScreen: Readonly<Record<string, string>>) => run(async () => savePlan(visit, await renderNearmap(frame, onScreen), 'Nearmap', { width: frame.width, height: frame.height }, frame));
  const pickImage = (file: File) => run(() => savePickedPlan(visit, file));
  const here = positionNow();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link to={`/job/${jobId}`} className="flex items-center gap-1 text-sm text-slate-600">
          <ArrowLeft className="h-4 w-4" /> {data.job.number}
        </Link>
        <h2 className="text-base font-semibold text-slate-900">{data.job.number} · Site plan</h2>
      </div>

      {(!plan || making) && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={making === 'nearmap' ? 'default' : 'outline'} disabled={!online || saving} onClick={() => (here ? setMaking('nearmap') : setTick((t) => t + 1))}>
            Nearmap
          </Button>
          <label className={`inline-flex min-h-10 cursor-pointer items-center rounded-md border border-slate-300 px-4 text-sm ${online && !saving ? '' : 'pointer-events-none opacity-50'}`}>
            Choose an image (Photos or Files)
            <input type="file" accept="image/*" className="hidden" disabled={saving} onChange={(e) => e.target.files?.[0] && void pickImage(e.target.files[0])} />
          </label>
          {plan && <Button variant="ghost" disabled={saving} onClick={() => setMaking(null)}>Keep the current plan</Button>}
          {!online && <Notice>Making a plan needs signal. Pins can be placed without it.</Notice>}
          {online && !here && <span className="text-xs text-slate-500">Nearmap starts where the iPad is: allow location, wait a moment outdoors, then tap Nearmap again.</span>}
        </div>
      )}

      {making === 'nearmap' && here && <NearmapFramer at={here} saving={saving} onSave={(f, onScreen) => void saveNearmap(f, onScreen)} />}
      {saving && (
        <p role="status" className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> Saving the plan to SharePoint. This can take a few minutes: keep this screen open until it&rsquo;s done.
        </p>
      )}
      {problem && <ErrorMessage>{problem}</ErrorMessage>}

      {plan && !making && !fileMissing && image && (
        <div className="flex gap-2" role="group" aria-label="What to mark on the plan">
          <Button variant={mode === 'pins' ? 'default' : 'outline'} className="h-11" aria-pressed={mode === 'pins'} onClick={() => setMode('pins')}>
            Photo pins
          </Button>
          <Button variant={mode === 'access' ? 'default' : 'outline'} className="h-11" aria-pressed={mode === 'access'} onClick={() => setMode('access')}>
            Access
          </Button>
        </div>
      )}

      {plan && !making && !fileMissing && image && mode === 'access' && (
        <div className="space-y-2">
          <AccessPlanView plan={plan} image={image} areas={externalAreas} lines={accessLines} onDraw={(area, line) => addAccessLine(visit, area, line)} onRemove={(id, lineId) => void removeAccessLine(id, lineId)} />
          <MarkedUpStatusLine {...access} online={online} wording={ACCESS_WORDING} />
        </div>
      )}

      {plan && !making && (fileMissing || !image || mode === 'pins') && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="space-y-2">
            {fileMissing ? (
              <PlanFileMissingNotice online={online} onMakeNew={() => setMaking('choose')} />
            ) : image ? (
              <SitePlanView
                plan={plan}
                image={image}
                pins={pins.map((p) => p.pin)}
                placing={placing}
                onPlace={(id, x, y) => {
                  void placePin(id, x, y, true);
                  setPlacing(null);
                }}
                onTurn={(id, bearing) => void setPinBearing(id, bearing)}
              />
            ) : (
              <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">{online ? 'Loading the plan…' : 'This plan was made on another device. Open it here once with signal.'}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {!fileMissing && (
                <>
                  <span>Saved to SharePoint ({plan.source}).</span>
                  <OutboundLink openIn="edge" href={plan.webUrl} className="text-primary underline">Open it there</OutboundLink>
                </>
              )}
              <button type="button" className="ml-auto text-primary underline" disabled={!online} onClick={() => setMaking('choose')}>
                Make a new plan
              </button>
            </div>
            <MarkedUpStatusLine {...markedUp} online={online} />
          </div>
          <div>
            <p className="mb-1 text-xs text-slate-500">External photos. Drag a pin to move it, or choose a photo and tap the plan to place its pin. Tap a pin to set the way it faces.</p>
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white text-sm">
              {pins.map(({ pin, label }) => (
                <li key={pin.id}>
                  <button type="button" onClick={() => setPlacing(placing === pin.id ? null : pin.id)} className={`flex w-full items-center gap-2 px-3 py-2 text-left ${placing === pin.id ? 'bg-orange-50' : ''}`}>
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: pin.colour }}>
                      {pin.number}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    <span className="shrink-0 text-xs text-slate-500">{pin.x === null ? (placing === pin.id ? 'tap the plan' : 'no pin') : ''}</span>
                  </button>
                </li>
              ))}
              {pins.length === 0 && <li className="px-3 py-2 text-slate-500">No photos of external areas yet.</li>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
